import { FILE_FORMATS } from '@app/constants/commonConstants';
import { setError } from '@app/store/exception/actions';
import { ToastContext } from 'cat-ecommerce-alloy';
import { useContext } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import * as XLSX from 'xlsx';

import {
  getLastRowWithValues,
  processPaste,
  replaceTokensInString
} from '@app/utils';

const useFileUploader = ({
  defaultRowValues,
  errorInfo,
  setFieldValue,
  setStatus,
  shoppingCartMaxItems,
  values
}) => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const { createSuccessToast } = useContext(ToastContext);
  const addRow = (rowNum, data = defaultRowValues) => {
    if (rowNum < shoppingCartMaxItems) {
      setFieldValue(`items.${rowNum}`, { ...data, id: v4() });
    }
  };

  const handleFileData = file => {
    const numRows = values?.items?.length;
    const reader = new FileReader();
    reader.onload = () => {
      setStatus({ setIsLoadingFile: false });
      const workbook = XLSX.read(reader.result, {
        type: 'binary'
      });
      workbook.SheetNames.forEach(sheetName => {
        const csv = XLSX.utils.sheet_to_csv(workbook.Sheets[sheetName]);
        const parsedValuesArray = csv
          ?.replace(/(^\s+)|(\s+$)/g, '')
          ?.split('\n');
        const res = processPaste({
          addRow,
          defaultRowValues,
          dispatch,
          errorInfo: {
            ...errorInfo,
            message: replaceTokensInString(
              t('DRAG_DROP_ERROR_LINE_ITEM_LIMIT'),
              shoppingCartMaxItems
            )
          },
          event: {},
          fileData: parsedValuesArray.slice(1),
          maxRows: shoppingCartMaxItems,
          numRows,
          rowNum: getLastRowWithValues(values.items) + 1,
          setFieldValue,
          createSuccessToast,
          toastMessage: t('QUICK_ORDER_TOAST'),
          values
        });
        if (res?.error) {
          dispatch(
            setError(errorInfo.domain, errorInfo.path, {
              message: t('UNKNOWN_ERROR_MESSAGE')
            })
          );
        }
      });
    };
    reader.readAsBinaryString(file);
  };

  const handleFileUpload = files => {
    setStatus({ setIsLoadingFile: true });
    if (files.length > 1) {
      setStatus({ setIsLoadingFile: false });
      return dispatch(
        setError(errorInfo.domain, errorInfo.path, {
          message: t('DRAG_DROP_ERROR_FILE_LIMIT')
        })
      );
    }
    const fileExtension = files[0]?.name.split('.').slice(-1)[0];
    const isCsvFile = fileExtension === FILE_FORMATS.csv;
    const isXlsXlsxFile =
      fileExtension === FILE_FORMATS.xls || fileExtension === FILE_FORMATS.xlsx;
    const isValidFile = isCsvFile || isXlsXlsxFile;
    const fileSizeKb = files[0].size / 1000;

    if (!isValidFile) {
      setStatus({ setIsLoadingFile: false });
      return dispatch(
        setError(errorInfo.domain, errorInfo.path, {
          message: t('QUICK_UPLOAD_FORMAT_ERROR_ALL_FORMATS')
        })
      );
    }
    if (isCsvFile && fileSizeKb > 5) {
      setStatus({ setIsLoadingFile: false });
      return dispatch(
        setError(errorInfo.domain, errorInfo.path, {
          message: replaceTokensInString(
            t('QUICK_ORDER_UPLOAD_ERROR_CSV'),
            files[0].name
          )
        })
      );
    }
    if (isXlsXlsxFile && fileSizeKb > 40) {
      setStatus({ setIsLoadingFile: false });
      return dispatch(
        setError(errorInfo.domain, errorInfo.path, {
          message: replaceTokensInString(
            t('QUICK_ORDER_UPLOAD_ERROR_XLS'),
            files[0].name
          )
        })
      );
    }
    handleFileData(files[0]);
  };
  return { handleFileUpload };
};

export default useFileUploader;
